import { useState } from "react"
import { Badge, Col, Container, Row } from "react-bootstrap"
import { useParams } from "react-router"
import { BookingEntry } from "../../../Components/Bookings/BookingEntry/BookingEntry"
import useMyProperties from "../../../Hooks/UseMyProperties/UseMyPropertiesHook"
import BookingGuestEntry from "../../../Components/BookingGuests/BookingGuestEntry/BookingGuestEntry"
import PurchaseEntry from "../../../Components/Purchases/PurchaseEntry"
import canAccess from "../../../services/accessService/accessService"
import AccessClickButton from "../../../Components/Buttons/AccessClickButton"
import BookingModal, { useBookingModal } from "../../../Components/Bookings/BookingModal/BookingModal"
import { FRONTEND_URL } from "../../../Config/config"
import { convertStringToJSON } from "../../../Helpers/Strings/stringHelpers"
import { useAddNewTurnoverMutation, useEditBookingMutation, useGetBookingByIdQuery, useGetBookingGuestsQuery, useGetPurchasesQuery } from "../../../services/bClientApi"
import { airBnbReservationDetailsLink } from "../../../Helpers/Booking/airbnbReservationDetails"
import { Link } from "react-router-dom"
import DataLogList from "../../../Components/DataLogs/DataLogList"
import { accountSelector } from "../../../redux/account/accountSlice"
import { useSelector } from "react-redux"
import useRelatedBookings from "../../../Hooks/UseRelatedBookings/UseRelatedBookings"
import { PropertyBookingEntry } from "../../../Types/bookingTypes"
import { isParent } from "../../../Helpers/Property/isParent"
import BookingParking from "../../../Components/Parking/BookingParking/BookingParking"
import BookingEmailActions from "../../../Components/Bookings/BookingEmailActions/BookingEmailActions"
import BookingCleanings from "../../../Components/Bookings/BookingCleanings/BookingCleanings"
import BookingEarnings from "../../../Components/Bookings/BookingEarnings/BookingEarnings"
import BookingFees from "../../../Components/Bookings/BookingFees/BookingFees"
import { vrboReservationDetailsLink } from "../../../Helpers/Booking/vrboReservationDetails"
import { ConfirmModal } from "../../../Components/Modals/confirmModal"
import ExtendStayModal from "../../../Components/Bookings/ExtendStayModal/ExtendStayModal"
import CancelBookingModal from "../../../Components/Bookings/CancelBookingModal/CancelBookingModal"

export default function BookingPage() {

  const {
    email: myEmail,
    _id: myAccountId,
  } = useSelector(accountSelector)

  const [addNewTurnover] = useAddNewTurnoverMutation()

  const { myProperties } = useMyProperties()
  const { bookingId = '' } = useParams()

  const {
    data,
    // isLoading,
    error,
  } = useGetBookingByIdQuery(bookingId)
  const bookingData = data || {}

  const [editBooking] = useEditBookingMutation()

  const {
    editBookingModal,
    clearBookingModal,
    onEditClick,
    onCheckOutClick,
    bookingModalonSubmit
  } = useBookingModal()

  const propertyData = myProperties[bookingData?.propertyId] || {}

  const {
    data: bookingGuestData,
  } = useGetBookingGuestsQuery({
    bookingGuestsQuery: {
      bookingId: bookingId,
    },
    pagination: {
      page: 1,
      limit: 1,
    }
  }, {
    skip: !bookingId || Object.keys(myProperties).length === 0,
  })

  const relatedBookings = useRelatedBookings(bookingId, bookingData, myProperties)

  const bookingGuestsList = bookingGuestData?.docs || []
  const bookingGuestInfo = bookingGuestsList && bookingGuestsList[0] && bookingGuestsList[0].bookingId === bookingId ? bookingGuestsList[0] : null
  const bookingGuestLink = `${FRONTEND_URL}/guest/confirm-booking?sourceConfirmation=${bookingData.sourceConfirmation}`
  const bookingDetailsLink = `${FRONTEND_URL}/booking-details/${bookingId}`

  const purchaseRefId = bookingData?.sourceConfirmation
  const {
    data: purchaseData,
    // isLoading: purchasesLoading,
    // error: purchasesError,
  } = useGetPurchasesQuery({
    purchasesQuery: {
      // productId: bookingId
      referenceNumber: purchaseRefId,
      sessionStatus: 'complete',
    },
    pagination: {
      limit: 100,
      page: 1,
    }
  }, {
    skip: !purchaseRefId || Object.keys(myProperties).length === 0,
  })
  const purchasesList = purchaseData?.docs || []
  let purchaseInfo: any[] = []
  if (purchasesList && purchasesList[0] && purchasesList[0].products && purchasesList[0].products.length > 0) {
    purchaseInfo = purchasesList
  }

  let emailAddress = ''
  if (purchaseInfo && purchaseInfo[0] && purchaseInfo[0].email) {
    emailAddress = purchaseInfo[0].email
  }
  if (!emailAddress && bookingData?.contactInfo?.email) {
    emailAddress = bookingData.contactInfo.email
  }

  let earlyCheckInAvailable = false
  let lateCheckOutAvailable = false
  let cleaningAvailable = propertyData?.services?.additionalCleaning === true

  if (propertyData?.linkedServices) {
    propertyData.linkedServices.forEach((service: any) => {
      const {
        linkedService,
      } = service

      if (linkedService === 'earlyCheckIn') {
        earlyCheckInAvailable = true
      }

      if (linkedService === 'lateCheckOut') {
        lateCheckOutAvailable = true
      }

    })
  }

  const [createTurnoverModal, setCreateTurnoverModal] = useState(false)
  const [cancelBookingModal, setCancelBookingModal] = useState(false)
  const [extendStayModal, setExtendStayModal] = useState(false)
  const cancelableStatuses = ['request', 'new', 'pending', 'confirmed']
  const canCancelBooking = cancelableStatuses.includes(bookingData?.bookingStatus)

  const createTurnover = async () => {
    setCreateTurnoverModal(false)
    const newTurnover = await addNewTurnover({
      propertyId: bookingData.propertyId,
      bookingId: bookingData._id,
      type: 'checkOut',
      status: 'new',
      scheduledFor: bookingData.checkOut,
      createdBy: myAccountId,
    }).unwrap()
    await editBooking({
      bookingId: bookingData._id,
      bookingInfo: {
        turnoverId: newTurnover._id,
      }
    }).unwrap()
  }

  const beforeCheckout = bookingData && bookingData.checkOut && new Date(bookingData.checkOut) > new Date()
  const afterCheckin = bookingData && bookingData.checkIn && new Date(bookingData.checkIn) < new Date()
  const checkedIn = beforeCheckout && afterCheckin

  const canEditBooking = canAccess('routes', 'edit-booking', { payload: bookingData })

  let remoteLockPayload: {
    userId?: string,
    pin?: string,
    checkIn?: string,
    checkOut?: string,
  } = {}

  let lateCheckOutDisabled = false
  let earlyCheckInDisabled = false

  if (bookingData && bookingData.linkedServices && bookingData.linkedServices.length > 0) {
    bookingData.linkedServices.forEach((service: any) => {
      if (service.linkedService === 'remoteLock') {
        remoteLockPayload = convertStringToJSON(service.linkedServiceId)
        return
      }

      if (service.linkedService === 'disableLateCheckOut') {
        lateCheckOutDisabled = true
      }
      if (service.linkedService === 'disableEarlyCheckIn') {
        earlyCheckInDisabled = true
      }
    })
  }

  const airbnbReservationDetails = airBnbReservationDetailsLink(bookingData)
  const vrboReservationDetails = vrboReservationDetailsLink(bookingData)

  const scrapeId = bookingData?.scrapeId
  const bookingTurnoverId = bookingData?.turnoverId

  return (
    <Container>
      <Row>
        <Col
          xs={12}
          lg={9}
        >
          <h1>Booking Information</h1>
          <hr />
          {error && (<>{JSON.stringify(error)}<br /></>)}


          {relatedBookings && relatedBookings.length > 0 && (
            <div className="related-bookings">
              <h3>Related Bookings</h3>
              <ul>
                {relatedBookings.map((booking: PropertyBookingEntry) => {
                  const propertyInfo = myProperties[booking.propertyId]
                  const duplicateRelated = booking.propertyId === bookingData.propertyId
                  return (
                    <li key={booking._id}>
                      <Link to={`/booking/${booking._id}`}>
                        {propertyInfo.title}
                      </Link>  {isParent(myProperties[booking.propertyId], myProperties[bookingData.propertyId]) && (
                        <span className="parent-property"> - Parent</span>
                      )}

                      {duplicateRelated && (
                        <Badge bg="danger">DUPLICATE</Badge>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {bookingId && bookingData && bookingData._id && (
            <div className="booking-info">
              <BookingEntry
                bookingInfo={bookingData}
                propertyName={propertyData?.title ? propertyData.title : `Property ${bookingData.propertyId}`}
                options={{
                  details: true,
                  disableLinks: {
                    view: true,
                    delete: true,
                  }
                }}
                extraButtons={[
                  ...(
                    canEditBooking &&
                      canAccess('booking', 'edit-booking-time') ?
                      [
                        {
                          label: 'Edit🕒',
                          variant: 'primary',
                          onClick: () => onEditClick(bookingData)
                        },
                        ...(checkedIn ? [{
                          label: 'Check Out⚡',
                          variant: 'secondary',
                          onClick: () => onCheckOutClick(bookingData)
                        }] : [])
                      ] : []
                  ),

                  ...(
                    canEditBooking &&
                      canCancelBooking ? [
                      {
                        label: 'Cancel Booking',
                        variant: 'danger',
                        onClick: () => setCancelBookingModal(true),
                      }
                    ] : []),

                  ...(
                    canEditBooking &&
                      canAccess('booking', 'edit-booking-time') ? [
                      {
                        label: 'Add Extension',
                        variant: 'success',
                        onClick: () => setExtendStayModal(true),
                      }
                    ] : []),
                ]}
              />

              {remoteLockPayload.userId && remoteLockPayload.pin && (
                <>
                  <h3>Remote Lock Information</h3>
                  <p>
                    <b>Remote Lock User Id:</b> <a href={`https://connect.remotelock.com/access/user/${remoteLockPayload.userId}`} target="_blank" rel="noreferrer">{remoteLockPayload.userId}</a><br />
                    <b>Remote Lock Pin:</b> {remoteLockPayload.pin}<br />
                  </p>
                </>
              )}


              {bookingData && bookingData.notes && (
                <>
                  <h3>Booking Notes</h3>
                  <p className="keep-whitespace">
                    {bookingData.notes}
                  </p>
                  <br />
                </>
              )}


              <BookingEmailActions
                bookingData={bookingData}
                emailAddress={emailAddress}
                myEmail={myEmail}
              />


              <div className="mb-4">
                {(canAccess('routes', 'booking-guest') || canAccess('booking', 'bookingGuestLink', { payload: bookingData })) && (
                  <>
                    <h3>Booking Guest Link</h3>
                    <a href={bookingGuestLink} target="_blank" rel="noreferrer" className="mb-2">
                      {bookingGuestLink}
                    </a>
                  </>
                )}

                {canAccess('routes', 'booking-guest') && canEditBooking && bookingGuestInfo && !bookingData.bookingGuestInfoId && (
                  <>
                    <br /> <br />
                    <AccessClickButton
                      variant="primary"
                      text="Link Guest Info"
                      resource="routes"
                      action="edit-booking"
                      onClick={() => {
                        editBooking({
                          bookingId: bookingData._id,
                          bookingInfo: {
                            bookingGuestInfoId: bookingGuestInfo._id,
                          }
                        })
                      }}
                    />
                  </>
                )}
              </div>

              {canAccess('booking', 'airbnbReservationDetails') && airbnbReservationDetails && (
                <div className="mb-4">
                  <h3>Airbnb Reservation Details</h3>
                  <a href={airbnbReservationDetails} target="_blank" rel="noreferrer" className="ellipsis-link">
                    {airbnbReservationDetails}
                  </a>
                </div>
              )}
              {canAccess('booking', 'vrboReservationDetails') && vrboReservationDetails && (
                <div className="mb-4">
                  <h3>VRBO Reservation Details</h3>
                  <a href={vrboReservationDetails} target="_blank" rel="noreferrer" className="ellipsis-link">
                    {vrboReservationDetails}
                  </a>
                </div>
              )}
            </div>
          )}

          <BookingParking
            bookingData={bookingData}
            propertyData={propertyData}
          />

          {canAccess('booking', 'edit-booking-time') && (earlyCheckInAvailable || lateCheckOutAvailable) && (
            <div className="mb-4">
              <h3>Check In/Out Modifications</h3>
              {earlyCheckInAvailable && canEditBooking && (
                <AccessClickButton
                  variant={earlyCheckInDisabled ? 'outline-danger' : 'outline-success'}
                  text={earlyCheckInDisabled ? '🚫 Early Check In' : '✅ Early Check In'}
                  resource="routes"
                  size="sm"
                  action="edit-booking"
                  className="me-2 mb-2"
                  onClick={async () => {
                    let updatedLinkedServices = bookingData.linkedServices.filter((service: any) => service.linkedService !== 'disableEarlyCheckIn')
                    if (!earlyCheckInDisabled) {
                      // add disableEarlyCheckIn service
                      updatedLinkedServices.push({
                        linkedService: 'disableEarlyCheckIn',
                        linkedServiceId: JSON.stringify({}),
                      })
                    }
                    await editBooking({
                      bookingId: bookingData._id,
                      bookingInfo: {
                        linkedServices: updatedLinkedServices
                      }
                    }).unwrap()
                  }}
                />
              )}
              {lateCheckOutAvailable && canEditBooking && (
                <AccessClickButton
                  variant={lateCheckOutDisabled ? 'outline-danger' : 'outline-success'}
                  text={lateCheckOutDisabled ? '🚫 Late Check Out' : '✅ Late Check Out'}
                  resource="routes"
                  size="sm"
                  action="edit-booking"
                  className="me-2 mb-2"
                  onClick={async () => {
                    let updatedLinkedServices = bookingData.linkedServices.filter((service: any) => service.linkedService !== 'disableLateCheckOut')
                    if (!lateCheckOutDisabled) {
                      // add disableLateCheckOut service
                      updatedLinkedServices.push({
                        linkedService: 'disableLateCheckOut',
                        linkedServiceId: JSON.stringify({}),
                      })
                    }
                    await editBooking({
                      bookingId: bookingData._id,
                      bookingInfo: {
                        linkedServices: updatedLinkedServices
                      }
                    }).unwrap()
                  }}
                />
              )}
            </div>
          )}

          <BookingCleanings
            bookingData={bookingData}
            propertyData={propertyData}
            relatedBookings={relatedBookings}
            myProperties={myProperties}
            cleaningAvailable={cleaningAvailable}
          />

          {canAccess('routes', 'turnover') && (
            <div className="mb-4">
              <h3>Turnover</h3>
              {bookingTurnoverId ? (
                <Link to={`/turnover/${bookingTurnoverId}`}>{bookingTurnoverId}</Link>
              ) : (
                canEditBooking && (
                  <AccessClickButton
                    variant="outline-primary"
                    text="Create Turnover"
                    resource="routes"
                    action="edit-booking"
                    onClick={() => setCreateTurnoverModal(true)}
                  />
                )
              )}
            </div>
          )}

          {bookingData && bookingData._id && (
            <BookingEarnings bookingData={bookingData} />
          )}

          {bookingData && bookingData._id && (
            <BookingFees bookingData={bookingData} />
          )}

          {scrapeId && canAccess('routes', 'scrape-entry') && (
            <div className="mb-4">
              <h3>Scrape Entry</h3>
              <Link to={`/scrape-entry/${scrapeId}`}>{scrapeId}</Link>
            </div>
          )}

          {canAccess('routes', 'booking-guest') && bookingGuestInfo && (
            <>
              <div className="mb-4">
                <h3>Booking Details Page</h3>
                <a href={bookingDetailsLink} target="_blank" rel="noreferrer" className="mb-2 ellipsis-link">
                  {bookingDetailsLink}
                </a>
              </div>

              <div className="mb-4">
                <div className="booking-guest-info">
                  <h3>Guest Info</h3>
                  <BookingGuestEntry
                    bookingGuestInfo={bookingGuestInfo}
                    propertyData={propertyData}
                  />
                </div>
              </div>

            </>
          )}

          {canAccess('routes', 'purchase') && purchaseInfo && purchaseInfo.length > 0 && (
            <div className="mb-4">
              <h3>Purchase Info</h3>
              {purchaseInfo.map((purchase) => (
                <PurchaseEntry
                  key={purchase._id}
                  purchaseData={purchase}
                />
              ))}
            </div>
          )}
        </Col>

        <Col
          xs={12}
          lg={3}
        >
          <DataLogList
            key={`id-${bookingId}-type-booking`}
            dataType="booking"
            dataId={bookingId}
          />
        </Col>
      </Row>

      <BookingModal
        active={editBookingModal.active}
        booking={editBookingModal.booking}
        clearBookingModal={clearBookingModal}
        bookingModalonSubmit={bookingModalonSubmit}
      />

      <ExtendStayModal
        active={extendStayModal}
        booking={bookingData}
        clear={() => setExtendStayModal(false)}
      />

      <CancelBookingModal
        active={cancelBookingModal}
        booking={bookingData}
        clear={() => setCancelBookingModal(false)}
      />

      <ConfirmModal
        title="Create Turnover"
        message="Are you sure you want to create a turnover for this booking?"
        active={createTurnoverModal}
        onClose={() => setCreateTurnoverModal(false)}
        onConfirm={createTurnover}
      />
    </Container>
  )
}
